<template>
  <b-table
      :fields="fields"
      :items="items"
      @row-clicked="onClickRow"
      table-class="text-nowrap"
      responsive
      hover
  >
    <template #cell(public)="data">
      <template v-if="data.item.plan.type.code === 'membership' && data.item.plan.is_free && !data.item.plan.is_public && hasFreePublicMembershipPlan">
        No
      </template>
      <no-permission-wrapper
        v-else
        :show="!canUpdatePricingPlans"
      >
        <b-form-checkbox
            :checked="data.item.plan.is_public"
            @input="val => updatePlanPublicStatus(data.item.plan, val)"
            :disabled="!canUpdatePricingPlans"
        />
      </no-permission-wrapper>
    </template>
    <template #cell(type)="data">
      <b-badge v-if="data.item.plan.type.code === 'membership'" variant="success">Membership</b-badge>
      <b-badge v-else-if="data.item.plan.type.code === 'add-on'" variant="primary">Add-on</b-badge>
      <span v-else>{{ data.item.type }}</span>
      <b-badge v-if="data.item.plan.company_type.code === 'metrc'" variant="secondary">THC</b-badge>
      <b-badge v-else-if="data.item.plan.company_type.code === 'non-metrc'" variant="warning">Non-THC</b-badge>
      <span v-else>{{ data.item.type }}</span>
    </template>
    <template #cell(licenses)="data">
      <span v-if="data.item.plan.is_limit_licenses">{{ data.item.plan.licenses_limit }}</span>
      <span v-if="data.item.plan.allow_extra_licenses">/+{{ getFormattedPrice(data.item.plan.extra_license_price) }}</span>
    </template>
    <template #cell(billing)="data">
      <template v-if="!data.item.plan.is_free">
        <template v-if="data.item.plan.billing_frequency === 'recurring'">
          <span>Recurring ({{ getFormattedRecurringBilling(data.item.plan.billing_recurrence) }})</span>
        </template>
        <template v-else>
          <span>One Time</span>
        </template>
      </template>
      <template v-if="companyView">
        <template v-if="data.item.plan.pivot.last_payment_failed_at">
          <br>
          <strong class="text-danger">Payment failed</strong>
        </template>
        <template v-else-if="data.item.plan.pivot.last_paid_amount">
          <br>
          <strong class="text-success">
            <template v-if="data.item.plan.billing_frequency === 'recurring'">
              Last Paid:
            </template>
            <template v-else>
              Paid:
            </template>
            {{ getFormattedPrice(data.item.plan.pivot.last_paid_amount) }}</strong>
        </template>
      </template>
    </template>
    <template #cell(free_trial)="data">
      <template v-if="!data.item.plan.is_free && data.item.plan.is_free_trial">
        <span>{{ data.item.plan.free_trial_length }} {{ data.item.plan.free_trial_period }}s</span>
      </template>
    </template>
    <template #cell(rates_by_user)="data">
      <template v-if="!data.item.plan.is_free">
        <span>{{ data.item.rates_by_user ? 'Yes' : 'No' }}</span>
      </template>
    </template>
    <template #cell(rates_by_limit)="data">
      <template v-if="!data.item.plan.is_free">
        <span>{{ data.item.rates_by_limit ? 'Yes' : 'No' }}</span>
      </template>
    </template>
    <template #cell(plan_started)="data">
      {{ convertFromUtc(data.item.plan.pivot.starts_at, 'L') }}
    </template>
    <template #cell(next_charge)="data">
      {{ !data.item.plan.pivot.finishes_at && data.item.plan.pivot.next_charge_at ? convertFromUtc(data.item.plan.pivot.next_charge_at, 'L') : '' }}
    </template>
    <template #cell(plan_ends)="data">
      {{ data.item.plan.pivot.finishes_at ? convertFromUtc(data.item.plan.pivot.finishes_at, 'L') : '' }}
    </template>
    <template #cell(actions)="data">
      <no-permission-wrapper
          v-if="!data.item.plan.pivot.finishes_at && data.item.plan.pivot.next_charge_at"
          :show="!canCancelPricingPlans"
      >
        <b-btn
            variant="danger"
            @click="cancelPricingPlan(data.item.plan)"
            :disabled="!canCancelPricingPlans"
        >
          Cancel
        </b-btn>
      </no-permission-wrapper>
    </template>
  </b-table>
</template>

<script>
import {convertFromUtc, getFormattedPrice, getFormattedRecurringBilling} from "../helpers";
import {API_ROUTES} from "../api";
import NoPermissionWrapper from "./NoPermissionWrapper.vue";

export default {
  name: "AdminPricingPlansTable",
  components: {NoPermissionWrapper},
  emits: ['cancel'],
  props: {
    plans: Array,
    companyView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fields: this.getTableFields()
    }
  },
  computed: {
    canCancelPricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_cancel');
    },
    canUpdatePricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_update');
    },
    items() {
      return this.plans.map(plan => ({
        plan,
        id: plan.id,
        type: plan.type.name,
        name: plan.name,
        rate: plan.is_free ? 'free' : plan.amount,
        rates_by_user: plan.is_rates_by_user,
        rates_by_limit: plan.is_rates_by_limit,
        hub_addons: plan.is_hub_access ? plan.hub_addons.map(a => a.name).join(', ') : '',
        companies: plan.companies_count
      }));
    },
    hasFreePublicMembershipPlan() {
      return !!this.plans.find(p => p.is_free && p.type.code === 'membership');
    }
  },
  methods: {
    getFormattedRecurringBilling,
    convertFromUtc,
    getFormattedPrice,
    getTableFields() {
      let fields = [
        {
          key: "public",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "id",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer",
          label: "#"
        },
        {
          key: "type",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "rate",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer",
          formatter: (value) => value === 'free' ? 'Free' : getFormattedPrice(value)
        },
        {
          key: "licenses",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer",
          label: "Active Licenses"
        },
        {
          key: "rates_by_user",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "rates_by_limit",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "hub_addons",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "billing",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "free_trial",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
        {
          key: "companies",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        },
      ];

      if (this.companyView) {
        fields.splice(fields.findIndex(f => f.key === 'public'), 1);
        fields.splice(fields.findIndex(f => f.key === 'id'), 1);
        fields.splice(fields.findIndex(f => f.key === 'free_trial'), 1);
        fields.splice(fields.findIndex(f => f.key === 'companies'), 1);
        fields.push({
          key: "plan_started",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        });
        fields.push({
          key: "next_charge",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        });
        fields.push({
          key: "plan_ends",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        });
        fields.push({
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle cursor-pointer"
        });
      }

      return fields;
    },
    onClickRow(item) {
      let planId = item.id;
      this.$router.push({name: "admin-pricing-plan-view", params: {id: planId}});
    },
    updatePlanPublicStatus(pricingPlan, isPublic) {
      if (!this.canUpdatePricingPlans) {
        return this.$bvToast.toast('You do not have permission to update pricing plans.', {
          title: 'Denied!',
          variant: 'danger'
        })
      }
      this.$api.post(`${API_ROUTES.ADMIN_PRICING_PLANS}/${pricingPlan.id}/set-public`, {
        is_public: isPublic
      }).then(() => {

      }).catch(() => {

      });
    },
    cancelPricingPlan(pricingPlan) {
      if (!this.canCancelPricingPlans) {
        return this.$bvToast.toast('You do not have permission to cancel pricing plans.', {
          title: 'Denied!',
          variant: 'danger'
        })
      }
      this.$emit('cancel', pricingPlan);
    }
  },
}
</script>
